import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import { useSettingsStore } from './settingsStore';
import { effectiveRequiredFieldsForCountry, validateClientForContract } from '../utils/validateClientForContract';

interface GenerateSignatureUrlOptions {
  templateId?: string;
  notificationEmail?: string;
  notificationPhone?: string;
}

interface SignatureStore {
  loading: boolean;
  error: string | null;
  generateSignatureUrl: (dealId: string, clientId: string, templateIdOrOpts?: string | GenerateSignatureUrlOptions) => Promise<string | null>;
  checkSignatureStatus: (dealId: string) => Promise<'pending' | 'completed' | 'expired' | 'cancelled' | null>;
}

export const useSignatureStore = create<SignatureStore>((set) => ({
  loading: false,
  error: null,

  // Accetta sia la firma legacy (templateId string) sia il nuovo shape options.
  generateSignatureUrl: async (dealId: string, clientId: string, templateIdOrOpts?: string | GenerateSignatureUrlOptions) => {
    const opts: GenerateSignatureUrlOptions =
      typeof templateIdOrOpts === 'string'
        ? { templateId: templateIdOrOpts }
        : (templateIdOrOpts ?? {});
    const templateId = opts.templateId;
    const notificationEmail = opts.notificationEmail?.trim() || undefined;
    const notificationPhone = opts.notificationPhone?.trim() || undefined;

    // GATE dati cliente (FUORI dal try così l'errore propaga al chiamante con il
    // messaggio specifico): blocca la creazione del link firma se mancano i campi
    // obbligatori configurati in settings.required_client_fields_for_contract.
    {
      const requiredFields = (useSettingsStore.getState().settings as any)
        ?.required_client_fields_for_contract as string[] | undefined;
      if (requiredFields && requiredFields.length > 0) {
        const { data: clientData } = await supabase
          .from('clients')
          .select('business_name, vat_number, tax_code, sdi_code, pec, email, email_amministrazione, street_address, postal_code, city, province, country, client_contacts!client_contacts_client_id_fkey(area, email)')
          .eq('id', clientId)
          .maybeSingle();

        // Contatti/Aziende first-class: il contatto amministrazione può essere un
        // referente (client_relationships). Merge per non bloccare a torto i clienti nuovi.
        if (clientData) {
          const { fetchReferentiAsContacts, mergeContactsByEmail } = await import('../utils/fetchReferentiContacts');
          const refMap = await fetchReferentiAsContacts([clientId]);
          (clientData as any).client_contacts = mergeContactsByEmail(
            ((clientData as any).client_contacts ?? []) as any,
            refMap[clientId] ?? [],
          );
        }

        // Il token whatsapp_group valida campi che vivono sul DEAL, non sul
        // cliente: vanno iniettati nel record come fa useContractClientGate,
        // altrimenti il gate fallisce sempre anche a dati compilati.
        let record: Record<string, any> | null = clientData;
        if (requiredFields.includes('whatsapp_group') && clientData) {
          const { data: dealData } = await supabase
            .from('deals')
            .select('whatsapp_group_numbers, whatsapp_group_name, whatsapp_reuse_group_jid')
            .eq('id', dealId)
            .maybeSingle();
          record = {
            ...clientData,
            whatsapp_group_numbers: (dealData?.whatsapp_group_numbers as string[] | null) ?? [],
            whatsapp_group_name: (dealData?.whatsapp_group_name as string | null) ?? '',
            // Riuso gruppo esistente: il validatore bypassa numeri+nome se presente.
            whatsapp_reuse_group_jid: (dealData as any)?.whatsapp_reuse_group_jid ?? null,
          };
        }

        // Clienti esteri riconosciuti: SDI, PEC e CF esonerati automaticamente.
        const effectiveFields = effectiveRequiredFieldsForCountry(
          requiredFields,
          (clientData as any)?.country,
        );
        const validation = validateClientForContract(record, effectiveFields);
        if (!validation.isValid) {
          throw new Error(
            `Dati cliente incompleti: manca ${validation.missingLabels.join(', ')}. Completa la scheda cliente prima di generare il link di firma.`,
          );
        }
      }
    }

    try {
      // Generate a unique identifier
      const uniqueId = Math.random().toString(36).substring(2, 15);
      
      console.log(`Signature Store: Generating URL with templateId: ${templateId}`);
      
      // organization_id obbligatorio: l'endpoint pubblico /api/documents/quote/public
      // restituisce 500 'Signature request invalid' se la signature_request lo ha NULL.
      // Lo prendiamo dal settingsStore (già caricato all'avvio dopo login).
      const organizationId = useSettingsStore.getState().organizationId;
      if (!organizationId) {
        throw new Error('Organization not found — utente non autenticato?');
      }

      // Create a signature request
      // Prepare base request data
      const requestData: any = {
        deal_id: dealId,
        client_id: clientId,
        organization_id: organizationId,
        status: 'pending',
        request_url: uniqueId,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
      };

      // notification_email / notification_phone: dove inviare il PDF firmato dopo
      // che il cliente ha completato la firma online. Letti da flusia-core
      // /api/documents/quote/public/notify e usati come destinatari email/WA.
      if (notificationEmail) {
        requestData.notification_email = notificationEmail;
      }
      if (notificationPhone) {
        requestData.notification_phone = notificationPhone;
      }

      // Add template_id only if provided - handles the case where the column might not exist
      if (templateId) {
        requestData.template_id = templateId;
        console.log(`Signature Store: Adding template_id ${templateId} to signature request`);
      }

      // We will try two approaches: first with template_id, then without if it fails
      try {
        let signatureRequestCreated = false;
        
        // First attempt - with template_id if provided
        if (templateId) {
          console.log(`Signature Store: First attempt - with template_id`);
          
          try {
            const { data, error } = await supabase
              .from('signature_requests')
              .insert(requestData)
              .select()
              .single();
              
            if (!error) {
              console.log(`Signature Store: Signature request created successfully with template_id`);
              signatureRequestCreated = true;
            } else {
              // If we get a schema error, we'll continue to the fallback
              console.warn(`Signature Store: Cannot use template_id in signature_requests: ${error.message}`);
              // We'll try the fallback below
            }
          } catch (templateError) {
            // Will try without template_id below
            console.error('Signature Store: Failed with template_id, trying without it', templateError);
          }
        }
        
        // Second attempt - only if first attempt failed
        if (!signatureRequestCreated) {
          console.log(`Signature Store: Trying without template_id`);
          
          // Remove template_id from the request data
          const simpleRequestData = { ...requestData };
          delete simpleRequestData.template_id;
          
          try {
            const { data: signatureRequest, error: requestError } = await supabase
              .from('signature_requests')
              .insert(simpleRequestData)
              .select()
              .single();
              
            if (requestError) {
              console.error(`Signature Store: Failed even without template_id: ${requestError.message}`);
            } else {
              console.log(`Signature Store: Signature request created successfully without template_id`);
              signatureRequestCreated = true;
            }
          } catch (fallbackError) {
            console.error('Signature Store: Complete failure to create signature request', fallbackError);
          }
        }
        
        // At this point, we continue with the URL creation regardless of whether
        // the signature request was successfully created or not
        console.log(`Signature Store: Continuing with URL generation, request created: ${signatureRequestCreated}`);
        
      } catch (error) {
        console.error('Signature Store: Failed in signature request process', error);
        // Continue even if this fails - the signature URL can still work
      }
      
      // Update the deal with ONLY the signature request URL, NOT the template_id
      // This avoids issues if the template_id column doesn't exist in the deals table
      const { error: updateError } = await supabase
        .from('deals')
        .update({ 
          signature_request_url: uniqueId,
          // Removed the template_id from being updated in the deals table
        })
        .eq('id', dealId);
        
      if (updateError) throw updateError;
      
      console.log(`Signature Store: Deal updated successfully with signature URL and template ID`);
      
      // Return just the unique ID, not the full URL
      return uniqueId;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      console.error('Signature Store: Failed to generate signature URL', error);
      return null;
    }
  },

  checkSignatureStatus: async (dealId: string) => {
    try {
      const { data, error } = await supabase
        .from('signature_requests')
        .select('status')
        .eq('deal_id', dealId)
        .single();

      if (error) throw error;
      return data.status as 'pending' | 'completed' | 'expired' | 'cancelled';
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  }
}));